import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLabel } from 'victory';
import {
	Button,
	ButtonStack,
	DataTable,
	Dialog,
	Drawer,
	ExportButton,
	FilterFields,
	LogoOverlay,
	PageHeading,
	Panel,
	PrintableReportHeader,
	Tooltip,
	WorkingIndicator,
} from 'components';
import { getDowNameFromNumber } from 'utils';
import { SurgeonTableData, useSurgeonScorecardQuery } from 'store/services/SurgeonScorecardService';
import { useAppSelector, useGetSystemsQuery, useSystem } from 'store';
import { useFilters, useToast } from 'context';
import Logo from 'assets/images/branding/logo_jpg.jpg';

const victoryFontFamily = 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const getOverviewSurgeonValue = (overview: SurgeonTableData['overview'] | undefined, index: number) => {
	return overview?.[index]?.surgeon ?? '';
};

const getLeadingNumber = (value: string) => {
	const parsed = parseInt(value.split(' ')[0].replace('%', ''));
	return Number.isNaN(parsed) ? 0 : parsed;
};

const getCasesValue = (overview: SurgeonTableData['overview'] | undefined) => {
	return getOverviewSurgeonValue(overview, 0).split(' ')[0] ?? '';
};

const SelectedDateRangeDisplay = (props: { startDate: string; endDate: string }) => {
	return (
		<div className='flex text-center bg-blue-500 items-center justify-center h-10 rounded-md mr-2'>
			<span className='text-b3 font-semibold px-6 text-white'>{`${props.startDate} – ${props.endDate}`}</span>
		</div>
	);
};

const SurgeonTable = (props: {
	title: string;
	data: SurgeonTableData | undefined;
	startDate: string;
	endDate: string;
	lockTable: boolean;
	minLimit?: Date;
}) => {
	function truncate(str: string) {
		return str.length > 35 ? str.substring(0, 35) + '...' : str;
	}

	return (
		<div className={props.lockTable ? 'animate-pulse cursor-not-allowed' : ''}>
			<DataTable
				title={props.title}
				tooltipContent={`Use this page to track monthly operational performance metrics for an individual surgeon. Expand the rows for additional detail.`}
				goToHelpID='commonDefinitions'
				headerContentCenterAlignment='left'
				disablePagination
				disableRowCounter
				columns={[
					{
						accessorKey: 'metric',
						header: 'Metric',
						cell: ({ row }) => <span className='font-semibold'>{row.original.metric}</span>,
						enableGlobalFilter: false,
					},
					{
						accessorKey: 'surgeon',
						header: 'Surgeon',
						cell: ({ row }) => <span className='font-semibold'>{row.original.surgeon}</span>,
						enableGlobalFilter: false,
						enableSorting: false,
					},
				]}
				data={props.data?.overview ?? []}
				rowSubview={(row) => (
					<div className='flex'>
						{row.original.metric === 'Case Volume' && (
							<div className='bg-white p-4 pt-5 rounded-md h-fit'>
								<table>
									<colgroup span={2}></colgroup>
									<thead>
										<tr>
											<th
												colSpan={2}
												scope='colgroup'
												className='pb-4 text-left text-bluegray-900 uppercase text-p3 font-semibold'
											>
												Case Volume by DOW
											</th>
										</tr>
									</thead>
									<tbody>
										{props.data?.case_volume.dow.map((dowData, i) => {
											return (
												<tr key={i}>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100 whitespace-nowrap'>
														{getDowNameFromNumber(dowData.day_of_week)}
													</td>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100'>{dowData.volume}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
						{row.original.metric === 'Add-On Rate' && (
							<div className='bg-white p-4 pt-5 rounded-md h-fit'>
								<table>
									<colgroup span={2}></colgroup>
									<thead>
										<tr>
											<th
												colSpan={2}
												scope='colgroup'
												className='pb-4 text-left text-bluegray-900 uppercase text-p3 font-semibold'
											>
												Add-On Volume by DOW
											</th>
										</tr>
									</thead>
									<tbody>
										{props.data?.addon_rate.dow.map((addonData, i) => {
											return (
												<tr key={i}>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100 whitespace-nowrap'>
														{getDowNameFromNumber(addonData.day_of_week)}
													</td>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100'>{addonData.volume}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
						<table className='border-separate border-spacing-x-10 w-fit pt-4 h-fit'>
							{(row.original.metric === 'Case Volume' || row.original.metric === 'Add-On Rate') && (
								<>
									<thead>
										<tr>
											<th colSpan={2} scope='col' className='text-left text-bluegray-900'></th>
											<th colSpan={4} scope='col' className='text-center text-white bg-gray-200 rounded-md text-[0.9em] uppercase'>
												Averages
											</th>
										</tr>
										<tr>
											<th className='pb-4 text-left text-bluegray-900'>
												<span className='uppercase text-p3 font-semibold whitespace-nowrap'>
													Top{' '}
													{row.original.metric === 'Case Volume'
														? props.data?.case_volume.top5.length
														: props.data?.addon_rate.top5.length}{' '}
													Cases by Vol
												</span>
											</th>
											<th className='pb-4 text-left text-bluegray-900'>
												<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Vol.</span>
											</th>
											<th className='pb-4 text-left text-bluegray-900'>
												<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Pat-In to Pat-Out</span>
											</th>
											<th className='pb-4 text-left text-bluegray-900'>
												<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Pat-In to Proc-Start</span>
											</th>
											<th className='pb-4 text-left text-bluegray-900'>
												<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Proc-Start to Proc-Stop</span>
											</th>
											<th className='pb-4 text-left text-bluegray-900'>
												<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Proc-Stop to Pat-Out</span>
											</th>
										</tr>
									</thead>
								</>
							)}

							<tbody>
								{row.original.metric === 'Case Volume' &&
									props.data?.case_volume.top5.map((caseData, i) => {
										return (
											<tr key={i}>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 whitespace-nowrap'>
													{truncate(caseData.name)}
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>{caseData.volume}</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(caseData.avg)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(caseData.wheels_in_avg / 60)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(caseData.procedure_time_avg / 60)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(caseData.wheels_out_avg / 60)} min
												</td>
											</tr>
										);
									})}

								{row.original.metric === 'FCOTS Rate' && (
									<React.Fragment>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Number of On Time Starts
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.fcots_rate.number_of_first_cases_on_time}
											</td>
										</tr>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Number of First Cases
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.fcots_rate.number_of_first_cases}
											</td>
										</tr>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Average FCOTS Rate Delay
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.fcots_rate.avg_fcots_delay !== 0 ? `${props.data?.fcots_rate.avg_fcots_delay} min` : '0 min'}
											</td>
										</tr>
									</React.Fragment>
								)}

								{row.original.metric === 'Average Wheels Out to Wheels In Time' && (
									<>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Number of Turnovers
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.average_wheels_tot.number_of_turnovers}
											</td>
										</tr>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Turnovers On-Target
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.average_wheels_tot.number_turnovers_on_target}%
											</td>
										</tr>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Flip Rate
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.average_wheels_tot.flip_rate}%
											</td>
										</tr>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Avg. Case Vol. when Flipped
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.average_wheels_tot.avg_case_vol !== 0
													? `${props.data?.average_wheels_tot.avg_case_vol} Cases`
													: '0 Cases'}
											</td>
										</tr>
										{/* <tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Close to Cut Turnover
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100'>
												{props.data?.average_tot.close_cut_turnover} min
											</td>
										</tr> */}
									</>
								)}
								{row.original.metric === 'Average Proc Out to Proc In Time' && (
									<>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Number of Turnovers
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.average_surgeon_tot.number_of_turnovers}
											</td>
										</tr>
										<tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Turnovers On-Target
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>
												{props.data?.average_surgeon_tot.number_turnovers_on_target}%
											</td>
										</tr>
										{/* <tr>
											<td className='p-0 py-4 text-p3 border-b border-bluegray-100 uppercase font-semibold text-bluegray-900'>
												Close to Cut Turnover
											</td>
											<td className='p-0 py-4 text-p2 border-b border-bluegray-100'>
												{props.data?.average_tot.close_cut_turnover} min
											</td>
										</tr> */}
									</>
								)}
								{row.original.metric === 'Add-On Rate' &&
									props.data?.addon_rate.top5.map((addonData, i) => {
										return (
											<tr key={i}>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 whitespace-nowrap'>
													{truncate(addonData.name)}
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>{addonData.volume}</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(addonData.avg)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(addonData.wheels_in_avg / 60)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(addonData.procedure_time_avg / 60)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(addonData.wheels_out_avg / 60)} min
												</td>
											</tr>
										);
									})}
							</tbody>
						</table>
					</div>
				)}
			/>
		</div>
	);
};

const SurgeonTables = (props: {
	title: string;
	data: SurgeonTableData[] | undefined;
	startDate: string;
	endDate: string;
	lockTable: boolean;
	minLimit?: Date;
}) => {
	function truncate(str: string) {
		return str.length > 19 ? str.substring(0, 19) + '...' : str;
	}

	return (
		<div className={props.lockTable ? 'animate-pulse cursor-not-allowed' : ''}>
			<DataTable
				title={props.title}
				tooltipContent={
					'Use this page to track monthly operational performance metrics for an individual surgeon. Expand the rows for additional detail.'
				}
				disablePagination
				disableRowCounter
				columns={[
					{
						accessorKey: 'surgeon_name',
						header: 'Surgeon',
						enableGlobalFilter: true,
						cell: ({ row }) => <span className='font-semibold block w-52'>{row.original.surgeon_name}</span>,
					},
					{
						accessorKey: 'cases',
						header: 'Cases',
						cell: ({ row }) => <span>{getCasesValue(row.original.overview)}</span>,
						enableGlobalFilter: false,
						sortingFn: (rowA, rowB) => {
							const a = getLeadingNumber(getOverviewSurgeonValue(rowA.original.overview, 0));
							const b = getLeadingNumber(getOverviewSurgeonValue(rowB.original.overview, 0));
							return b > a ? 1 : -1;
						},
					},
					{
						accessorKey: 'fcots_rate',
						header: 'FCOTS Rate',
						cell: ({ row }) => <span>{getOverviewSurgeonValue(row.original.overview, 1)}</span>,
						enableGlobalFilter: false,
						meta: {
							headerClass: 'whitespace-pre-line',
						},
						sortingFn: (rowA, rowB) => {
							const a = getLeadingNumber(getOverviewSurgeonValue(rowA.original.overview, 1));
							const b = getLeadingNumber(getOverviewSurgeonValue(rowB.original.overview, 1));
							return b > a ? 1 : -1;
						},
					},
					{
						accessorKey: 'primetime_tot',
						header: 'Avg. Primetime TOT',
						cell: ({ row }) => <span>{getOverviewSurgeonValue(row.original.overview, 2)}</span>,
						enableGlobalFilter: false,
						meta: {
							headerClass: 'whitespace-pre-line',
						},
						sortingFn: (rowA, rowB) => {
							let a = getLeadingNumber(getOverviewSurgeonValue(rowA.original.overview, 2));
							let b = getLeadingNumber(getOverviewSurgeonValue(rowB.original.overview, 2));

							if (Number.isNaN(a)) {
								a = 0;
							}

							if (Number.isNaN(b)) {
								b = 0;
							}

							return b > a ? 1 : -1;
						},
					},
					{
						accessorKey: 'addon_rate',
						header: 'Add-On Rate',
						cell: ({ row }) => <span>{getOverviewSurgeonValue(row.original.overview, 4)}</span>,
						enableGlobalFilter: false,
						sortingFn: (rowA, rowB) => {
							const a = getLeadingNumber(getOverviewSurgeonValue(rowA.original.overview, 4));
							const b = getLeadingNumber(getOverviewSurgeonValue(rowB.original.overview, 4));
							return b > a ? 1 : -1;
						},
					},
					{
						accessorKey: 'first_on_time',
						header: 'On Time Starts',
						cell: ({ row }) => <span>{row.original.fcots_rate.number_of_first_cases_on_time}</span>,
						enableGlobalFilter: false,
						meta: {
							headerClass: 'whitespace-pre-line',
						},
						sortingFn: (rowA, rowB) => {
							const a = rowA.original.fcots_rate.number_of_first_cases_on_time;
							const b = rowB.original.fcots_rate.number_of_first_cases_on_time;
							return b > a ? 1 : -1;
						},
					},
					{
						accessorKey: 'first_cases',
						header: 'First Cases',
						cell: ({ row }) => <span>{row.original.fcots_rate.number_of_first_cases}</span>,
						enableGlobalFilter: false,
						meta: {
							headerClass: 'whitespace-pre-line',
						},
						sortingFn: (rowA, rowB) => {
							const a = rowA.original.fcots_rate.number_of_first_cases;
							const b = rowB.original.fcots_rate.number_of_first_cases;
							return b > a ? 1 : -1;
						},
					},
					{
						accessorKey: 'avg_fcots',
						header: 'Avg. FCOTS Delay',
						cell: ({ row }) => <span>{row.original.fcots_rate.avg_fcots_delay} Min</span>,
						enableGlobalFilter: false,
						meta: {
							headerClass: 'whitespace-pre-line',
						},
						sortingFn: (rowA, rowB) => {
							const a = rowA.original.fcots_rate.avg_fcots_delay;
							const b = rowB.original.fcots_rate.avg_fcots_delay;
							return b > a ? 1 : -1;
						},
					},
					{
						accessorKey: 'num_wheels_turnovers',
						header: 'Wheels Out Next Wheels In Turnovers',
						cell: ({ row }) => <span>{row.original.average_wheels_tot.number_of_turnovers}</span>,
						enableGlobalFilter: false,
						meta: {
							headerClass: 'whitespace-pre-line',
						},
						sortingFn: (rowA, rowB) => {
							const a = rowA.original.average_wheels_tot.number_of_turnovers;
							const b = rowB.original.average_wheels_tot.number_of_turnovers;
							return b > a ? 1 : -1;
						},
					},
					{
						accessorKey: 'num_surgeon_turnovers',
						header: 'Turnovers',
						cell: ({ row }) => <span>{row.original.average_surgeon_tot.number_of_turnovers}</span>,
						enableGlobalFilter: false,
						meta: {
							headerClass: 'whitespace-pre-line',
						},
						sortingFn: (rowA, rowB) => {
							const a = rowA.original.average_surgeon_tot.number_of_turnovers;
							const b = rowB.original.average_surgeon_tot.number_of_turnovers;
							return b > a ? 1 : -1;
						},
					},
				]}
				data={props.data ?? []}
				rowSubview={(row) => (
					<div className='flex flex-col'>
						{row.original.case_volume.top5.length > 0 && (
							<div className='flex'>
								<div className='bg-white p-4 pt-5 rounded-md h-fit'>
									<table>
										<colgroup span={2}></colgroup>
										<thead>
											<tr>
												<th
													colSpan={2}
													scope='colgroup'
													className='whitespace-nowrap pb-4 text-left text-bluegray-900 uppercase text-p3 font-semibold'
												>
													Case Volume by DOW
												</th>
											</tr>
										</thead>
										<tbody>
											{row.original.case_volume.dow.map((dowData, i) => {
												return (
													<tr key={i}>
														<td className='p-0 py-4 text-p2 border-b border-bluegray-100 whitespace-nowrap'>
															{getDowNameFromNumber(dowData.day_of_week)}
														</td>
														<td className='p-0 py-4 text-p2 border-b border-bluegray-100'>{dowData.volume}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>

								<table className='border-separate border-spacing-x-10 w-fit pt-4 h-fit'>
									<tr>
										<th colSpan={2} scope='col' className='text-left text-bluegray-900'></th>
										<th colSpan={4} scope='col' className='text-center text-white bg-gray-200 rounded-md text-[0.9em] uppercase'>
											Averages
										</th>
									</tr>
									<tr>
										<th className='pb-4 text-left text-bluegray-900'>
											<span className='uppercase text-p3 font-semibold whitespace-nowrap'>
												Top {row.original.case_volume.top5.length} Cases by Vol
											</span>
										</th>
										<th scope='col' className='pb-4 text-left text-bluegray-900'>
											<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Vol.</span>
										</th>
										<th scope='col' className='pb-4 text-left text-bluegray-900'>
											<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Pat-In to Pat-Out</span>
										</th>
										<th scope='col' className='pb-4 text-left text-bluegray-900'>
											<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Pat-In to Proc-Start</span>
										</th>
										<th scope='col' className='pb-4 text-left text-bluegray-900'>
											<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Proc-Start to Proc-Stop</span>
										</th>
										<th scope='col' className='pb-4 text-left text-bluegray-900'>
											<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Proc-Stop to Pat-Out</span>
										</th>
									</tr>

									{row.original.case_volume.top5.map((caseData, i) => {
										return (
											<tr key={i}>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 whitespace-nowrap'>
													<Tooltip content={caseData.name}>{truncate(caseData.name)}</Tooltip>
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>{caseData.volume}</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(caseData.avg)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(caseData.wheels_in_avg / 60)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(caseData.procedure_time_avg / 60)} min
												</td>
												<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
													{Math.round(caseData.wheels_out_avg / 60)} min
												</td>
											</tr>
										);
									})}
								</table>
							</div>
						)}
						<div className='h-0.5 my-8 bg-gray-200 rounded-lg w-100'></div>
						{row.original.addon_rate.top5.length > 0 && (
							<div className='flex'>
								<div className='bg-white p-4 pt-5 rounded-md h-fit'>
									<table>
										<colgroup span={2}></colgroup>
										<thead>
											<tr>
												<th
													colSpan={2}
													scope='colgroup'
													className='whitespace-nowrap pb-4 text-left text-bluegray-900 uppercase text-p3 font-semibold'
												>
													ADD-ON VOLUME BY DOW
												</th>
											</tr>
										</thead>
										<tbody>
											{row.original.addon_rate.dow.map((dowData, i) => {
												return (
													<tr key={i}>
														<td className='p-0 py-4 text-p2 border-b border-bluegray-100 whitespace-nowrap'>
															{getDowNameFromNumber(dowData.day_of_week)}
														</td>
														<td className='p-0 py-4 text-p2 border-b border-bluegray-100'>{dowData.volume}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>

								<table className='border-separate border-spacing-x-10 w-fit pt-4 h-fit'>
									<>
										<thead>
											<tr>
												<th colSpan={2} scope='col' className='text-left text-bluegray-900'></th>
												<th
													colSpan={4}
													scope='col'
													className='text-center text-white bg-gray-200 rounded-md text-[0.9em] uppercase'
												>
													Averages
												</th>
											</tr>
											<tr>
												<th className='pb-4 text-left text-bluegray-900'>
													<span className='uppercase text-p3 font-semibold whitespace-nowrap'>
														Top {row.original.addon_rate.top5.length} Add-On Cases by Vol
													</span>
												</th>
												<th className='pb-4 text-left text-bluegray-900'>
													<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Vol.</span>
												</th>
												<th className='pb-4 text-left text-bluegray-900'>
													<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Pat-In to Pat-Out</span>
												</th>
												<th className='pb-4 text-left text-bluegray-900'>
													<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Pat-In to Proc-Start</span>
												</th>
												<th className='pb-4 text-left text-bluegray-900'>
													<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Proc-Start to Proc-Stop</span>
												</th>
												<th className='pb-4 text-left text-bluegray-900'>
													<span className='uppercase text-p3 font-semibold whitespace-nowrap'>Proc-Stop to Pat-Out</span>
												</th>
											</tr>
										</thead>
									</>

									<tbody>
										{row.original.addon_rate.top5.map((caseData, i) => {
											return (
												<tr key={i}>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100 whitespace-nowrap'>
														<Tooltip content={caseData.name}>{truncate(caseData.name)}</Tooltip>
													</td>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center'>{caseData.volume}</td>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
														{Math.round(caseData.avg)} min
													</td>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
														{Math.round(caseData.wheels_in_avg / 60)} min
													</td>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
														{Math.round(caseData.procedure_time_avg / 60)} min
													</td>
													<td className='p-0 py-4 text-p2 border-b border-bluegray-100 text-center whitespace-nowrap'>
														{Math.round(caseData.procedure_time_avg / 60)} min
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						)}
					</div>
				)}
			/>
		</div>
	);
};

const splitOverviewValue = (value: string) => {
	const trimmedValue = value.trim();
	const match = trimmedValue.match(/^(-?\d+(?:\.\d+)?)(.*)$/);

	if (!match) {
		return { value: trimmedValue || '—', suffix: '' };
	}

	return {
		value: match[1],
		suffix: match[2].trim(),
	};
};

const formatMinutes = (value: number | undefined) => `${Math.round(value ?? 0)} min`;
const formatSecondsAsMinutes = (value: number | undefined) => `${Math.round((value ?? 0) / 60)} min`;

const truncateWithTooltip = (value: string, length = 31) => {
	const truncated = value.length > length ? `${value.substring(0, length)}...` : value;

	return <Tooltip content={value}>{truncated}</Tooltip>;
};

const ScorecardMetricCard = (props: { value: string; label: string }) => {
	const { value, suffix } = splitOverviewValue(props.value);

	return (
		<div className='flex flex-col flex-1 h-20 bg-blue-50 p-5 rounded-md border border-blue-500 text-center text-slate-500 justify-center'>
			<div className='font-semibold text-h3 whitespace-nowrap'>
				{value}
				{suffix && <span className='text-p2 font-semibold ml-1'>{suffix}</span>}
			</div>
			<div className='text-p3 whitespace-nowrap'>{props.label}</div>
		</div>
	);
};

const ProcedureVolumeTable = (props: { data: SurgeonTableData['case_volume']['top5']; isAddon?: boolean }) => {
	if (props.isAddon) {
		return (
			<DataTable
				disablePagination
				minimalStyle
				disableRowCounter
				title=''
				columns={[
					{
						header: 'Procedure',
						accessorKey: 'name',
						cell: ({ row }) => <p className='font-semibold'>{truncateWithTooltip(row.original.name, 37)}</p>,
					},
					{
						header: 'Volume',
						accessorKey: 'volume',
					},
					{
						header: 'Avg Duration',
						accessorKey: 'avg',
						cell: ({ row }) => <p>{formatMinutes(row.original.avg)}</p>,
					},
				]}
				data={props.data}
			/>
		);
	}

	return (
		<DataTable
			disablePagination
			minimalStyle
			disableRowCounter
			title=''
			columns={[
				{
					header: 'Procedure',
					accessorKey: 'name',
					cell: ({ row }) => (
						<p className='font-semibold'>{truncateWithTooltip(row.original.name, props.isAddon ? 37 : 31)}</p>
					),
				},
				{
					header: 'Vol',
					accessorKey: 'volume',
				},
				{
					header: 'Avg Dur',
					accessorKey: 'avg',
					cell: ({ row }) => <p>{formatMinutes(row.original.avg)}</p>,
				},
				{
					header: 'Avg Start',
					accessorKey: 'wheels_in_avg',
					cell: ({ row }) => <p>{formatSecondsAsMinutes(row.original.wheels_in_avg)}</p>,
				},
				{
					header: 'Avg End',
					accessorKey: 'wheels_out_avg',
					cell: ({ row }) => <p>{formatSecondsAsMinutes(row.original.wheels_out_avg)}</p>,
				},
			]}
			data={props.data}
		/>
	);
};

const DowBarList = (props: { data: SurgeonTableData['case_volume']['dow'] }) => {
	const chartData = props.data.map((item) => ({
		x: getDowNameFromNumber(item.day_of_week).substring(0, 3),
		y: item.volume,
	}));
	const maxVolume = Math.max(...chartData.map((item) => item.y), 1);
	const chartHeight = Math.max(chartData.length * 28, 90);

	return (
		<div className='text-slate-500 -mx-2 -my-2'>
			{props.data.length === 0 && <p className='text-p2 text-slate-400'>No day-of-week data found.</p>}
			{props.data.length > 0 && (
				<VictoryChart
					domain={{ y: [0, maxVolume * 1.08] }}
					domainPadding={{ x: 14 }}
					height={chartHeight}
					horizontal
					padding={{ top: 6, bottom: 6, left: 42, right: 42 }}
				>
					<VictoryAxis
						dependentAxis
						style={{
							axis: { stroke: 0 },
							grid: { stroke: '#e2e8f0', strokeDasharray: '4,4' },
							tickLabels: { fill: 'transparent' },
						}}
						tickFormat={() => ''}
					/>
					<VictoryAxis
						style={{
							axis: { stroke: 0 },
							tickLabels: { fontFamily: victoryFontFamily, fontSize: 14, fill: '#64748b', padding: 5 },
						}}
					/>
					<VictoryBar
						barRatio={0.75}
						barWidth={14}
						cornerRadius={{ top: 4, bottom: 4 }}
						data={chartData}
						style={{
							data: { fill: '#106ff4' },
							labels: { fill: '#334155', fontSize: 11, fontFamily: victoryFontFamily, fontWeight: 600 },
						}}
						labels={chartData.map((item) => item.y.toString())}
						labelComponent={<VictoryLabel dx={5} dy={1} direction='ltr' />}
					/>
				</VictoryChart>
			)}
		</div>
	);
};

const DetailRows = (props: { rows: { label: string; value: string | number }[] }) => {
	return (
		<DataTable
			disablePagination
			minimalStyle
			disableRowCounter
			title=''
			columns={[
				{
					header: 'Metric',
					accessorKey: 'label',
					cell: ({ row }) => <p className='text-slate-500'>{row.original.label}</p>,
				},
				{
					header: 'Value',
					accessorKey: 'value',
					cell: ({ row }) => <p className='font-semibold text-right'>{row.original.value}</p>,
					meta: {
						headerClass: 'text-right',
					},
				},
			]}
			data={props.rows}
		/>
	);
};

const SingleSurgeonScorecardDashboard = (props: { data: SurgeonTableData; lockTable: boolean }) => {
	const overview = props.data.overview;

	return (
		<div className={`flex flex-col surgeon-scorecard ${props.lockTable ? 'animate-pulse cursor-not-allowed' : ''}`}>
			<div className='h-10'></div>
			<div className='flex justify-between h-20 text-slate-500 gap-4'>
				<ScorecardMetricCard value={getOverviewSurgeonValue(overview, 0)} label='Volume' />
				<ScorecardMetricCard value={getOverviewSurgeonValue(overview, 1)} label='FCOTS Rate' />
				<ScorecardMetricCard value={getOverviewSurgeonValue(overview, 2)} label='Avg. Wheels Out to In' />
				<ScorecardMetricCard value={getOverviewSurgeonValue(overview, 3)} label='Avg. Proc Out to In' />
				<ScorecardMetricCard value={getOverviewSurgeonValue(overview, 4)} label='Add-On Rate' />
			</div>
			<div className='h-10'></div>

			<div className='grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4'>
				<Panel
					title={`Top ${props.data.case_volume.top5.length || 5} Procedures by Volume`}
					defaultTitleClass='font-semibold whitespace-nowrap overflow-hidden text-ellipsis'
				>
					<ProcedureVolumeTable data={props.data.case_volume.top5} />
				</Panel>
				<Panel title='Case Volume by Day' defaultTitleClass='font-semibold whitespace-nowrap overflow-hidden text-ellipsis'>
					<DowBarList data={props.data.case_volume.dow} />
				</Panel>
			</div>

			<div className='h-10'></div>

			<div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
				<Panel
					title='First Case On-Time Start Details'
					defaultTitleClass='font-semibold whitespace-nowrap overflow-hidden text-ellipsis'
				>
					<DetailRows
						rows={[
							{ label: 'On-Time Starts', value: props.data.fcots_rate.number_of_first_cases_on_time },
							{ label: 'First Cases', value: props.data.fcots_rate.number_of_first_cases },
							{ label: 'Avg. Delay', value: `${props.data.fcots_rate.avg_fcots_delay} min` },
						]}
					/>
				</Panel>
				<Panel
					title='Primetime Turnover Details'
					defaultTitleClass='font-semibold whitespace-nowrap overflow-hidden text-ellipsis'
				>
					<DetailRows
						rows={[
							{ label: 'Number of Turnovers', value: props.data.average_wheels_tot.number_of_turnovers },
							{ label: 'Turnovers On-Target', value: `${props.data.average_wheels_tot.number_turnovers_on_target}%` },
							{ label: 'Flip Rate', value: `${props.data.average_wheels_tot.flip_rate}%` },
							{ label: 'Avg. Case Vol. when Flipped', value: props.data.average_wheels_tot.avg_case_vol },
						]}
					/>
				</Panel>
			</div>

			<div className='h-10'></div>

			<div className='grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4'>
				<Panel
					title={`Top ${props.data.addon_rate.top5.length || 4} Add-On Procedures by Volume`}
					defaultTitleClass='font-semibold whitespace-nowrap overflow-hidden text-ellipsis'
				>
					<ProcedureVolumeTable data={props.data.addon_rate.top5} isAddon />
				</Panel>
				<Panel
					title='Add-On Volume by Day'
					defaultTitleClass='font-semibold whitespace-nowrap overflow-hidden text-ellipsis'
				>
					<DowBarList data={props.data.addon_rate.dow} />
				</Panel>
			</div>
		</div>
	);
};

const PrintableSurgeonScorecardPage = (props: {
	data: SurgeonTableData;
	lockTable: boolean;
	dateRangeLabel: string;
	facilitySystemLabel: string;
	isLastPage: boolean;
}) => {
	return (
		<div
			className='surgeon-scorecard-print-page bg-white'
			style={{
				breakAfter: props.isLastPage ? 'auto' : 'page',
				pageBreakAfter: props.isLastPage ? 'auto' : 'always',
			}}
		>
			<PrintableReportHeader>
				<p className='text-[2em] font-bold leading-tight text-bluegray-900'>{props.data.surgeon_name}</p>
				<p className='mt-2 text-[1.35em] font-medium text-bluegray-500'>{props.dateRangeLabel}</p>
				<p className='mt-1 text-[1.1em] font-medium text-bluegray-500'>{props.facilitySystemLabel}</p>
			</PrintableReportHeader>
			<SingleSurgeonScorecardDashboard data={props.data} lockTable={props.lockTable} />
		</div>
	);
};

export function SurgeonScorecard() {
	// Filters
	const {
		surgeons,
		dateRange,
		wheelsTurnoverTimeThreshold,
		fcotsGraceMinutes,
		procedures,
		resetFilters,
		applyFilters,
		clearFilters,
		filtersAreDirty,
		metadata,
		endDate,
		currentPageLoaded,
		filtersAreFetching,
	} = useFilters();

	// There is sometimes a delay in our filters when a user switches pages
	// (which is why we check if currentPageLoaded is equal to our current page),
	// To account for the delay, we tell our RTK Query to skip until we set skipRequest to false.
	const [skipRequest, setSkipRequest] = useState(true);
	useEffect(() => {
		setTimeout(() => {
			if (currentPageLoaded === '/surgeon-scorecard') {
				setSkipRequest(false);
			}
		}, 0);
	}, [currentPageLoaded]);

	const minLimit = new Date(`${endDate.getFullYear() - 5}-Jan`);
	const { selectedFacility } = useAppSelector((state) => state.userState);
	const { data, isFetching, error, isLoading } = useSurgeonScorecardQuery(
		{
			facility_id: selectedFacility,
			surgeon_id: surgeons?.applied.map((s) => s.id) ?? [],
			start_date: format(dateRange?.applied.startDate, 'M/d/yyyy'),
			end_date: format(dateRange?.applied.endDate, 'M/d/yyyy'),
			fcots_grace: fcotsGraceMinutes?.applied,
			tot_threshold: wheelsTurnoverTimeThreshold?.applied,
			procedures: procedures?.applied.map((procedure) => procedure.name),
		},
		{
			skip: surgeons?.applied?.map((s) => s.id).length < 1 || skipRequest || filtersAreFetching,
		}
	);

	const { data: userData } = useSystem();
	const { selectedSystem } = useAppSelector((state) => state.userState);
	const system = userData?.healthsystems.find((h) => h.id === selectedSystem);
	const { data: allFacilities } = useGetSystemsQuery({
		healthsystem_id: selectedSystem,
	});
	const current_facility = allFacilities?.facilities?.find((f) => f.id === selectedFacility);

	// Handle api response to create toast message on every error.
	const { createToast } = useToast();
	useEffect(() => {
		if (error) {
			if ('data' in error) {
				createToast({
					title: `${error.data}`,
					variant: 'error',
				});
			} else {
				createToast({
					title: `There was an error connecting to the server.`,
					variant: 'error',
				});
			}
		}
	}, [createToast, error]);

	const exportData =
		// eslint-disable-next-line
		data?.surgeonData
			.filter((v) => v.surgeon_name)
			.map((v) => ({
				surgeon_name: v.surgeon_name,
				cases: getCasesValue(v.overview),
				fcots_rate: getOverviewSurgeonValue(v.overview, 1),
				avg_primetime_tot: getOverviewSurgeonValue(v.overview, 2),
				addon_rate: getOverviewSurgeonValue(v.overview, 4),
				num_on_time_starts: v.fcots_rate.number_of_first_cases_on_time,
				num_first_cases: v.fcots_rate.number_of_first_cases,
				avg_fcots_delay_mins: v.fcots_rate.avg_fcots_delay,
				num_turnovers: v.average_wheels_tot.number_of_turnovers,
			})) ?? [];

	const [printing, setPrinting] = useState(false);
	const [isBulkPrinting, setIsBulkPrinting] = useState(false);
	const [bulkPrintProgress, setBulkPrintProgress] = useState(0);
	const [bulkPrintStatusText, setBulkPrintStatusText] = useState('');
	const printableSurgeonData = data?.surgeonData.filter((element) => element.surgeon_name !== '') ?? [];
	const dateRangeLabel = `${format(dateRange.applied.startDate, 'M/d/yyyy')} – ${format(
		dateRange.applied.endDate,
		'M/d/yyyy'
	)}`;
	const facilitySystemLabel = `${current_facility?.name ?? ''}${system?.name ? ` - ${system.name}` : ''}`;

	const generatePDF = async () => {
		const element = document.getElementById('surgeon-scorecard-report-container');
		const pageElements = Array.from(document.querySelectorAll<HTMLElement>('.surgeon-scorecard-print-page'));
		const elementsToPrint = pageElements.length > 0 ? pageElements : element ? [element] : [];
		const totalPages = elementsToPrint.length;

		if (elementsToPrint.length === 0) {
			setPrinting(false);
			setIsBulkPrinting(false);
			setBulkPrintProgress(0);
			setBulkPrintStatusText('');
			return;
		}

		try {
			setBulkPrintProgress(0);
			setBulkPrintStatusText(`Rendering page 1 of ${totalPages}`);
			const doc = new jsPDF();

			const html2canvasOptions = {
				useCORS: true,
				allowTaint: true,
				dpi: 200,
				scrollY: -window.scrollY,
			};

			for (let i = 0; i < elementsToPrint.length; i++) {
				setBulkPrintStatusText(`Rendering page ${i + 1} of ${totalPages}`);
				const canvas = await html2canvas(elementsToPrint[i], html2canvasOptions);
				const imageData = canvas.toDataURL('image/jpeg');

				if (i > 0) {
					doc.addPage();
				}

				doc.addImage(imageData, 'JPEG', 10, 10, 190, 0);
				setBulkPrintProgress(Math.round(((i + 1) / totalPages) * 100));
			}
			setBulkPrintStatusText('Finalizing PDF...');
			doc.save(`surgeon_scorecard_${current_facility?.name ?? 'report'}.pdf`);
		} catch (error) {
			console.error('Error generating PDF:', error); // keeping for easy error troubleshooting
		} finally {
			setPrinting(false);
			setIsBulkPrinting(false);
			setBulkPrintProgress(0);
			setBulkPrintStatusText('');
		}
	};

	return (
		<>
			<div id='surgeon-scorecard-report-container'>
				<Dialog
					sizeX='sm'
					open={isBulkPrinting}
					onOpenChange={() => {
						// Prevent manual close while bulk export is running.
					}}
					title='Generating PDF...'
					trigger={<></>}
					actionButtons={[]}
				>
					<p className='text-p2 text-slate-500'>{bulkPrintStatusText}</p>
					<div className='mt-4 h-3 w-full overflow-hidden rounded-full bg-slate-200'>
						<div
							className='h-full rounded-full bg-blue-500 transition-[width] duration-200'
							style={{ width: `${bulkPrintProgress}%` }}
						/>
					</div>
					<p className='mt-2 text-right text-p2 font-semibold text-slate-700'>{bulkPrintProgress}%</p>
				</Dialog>
				{!printing && (
					<PageHeading>
						{surgeons?.applied.length === 0
							? ''
							: surgeons?.applied.length === 1
							? surgeons?.applied[0].name
							: 'Surgeon Scorecard'}
					</PageHeading>
				)}
				{!printing && (
					<div className='flex justify-end pb-3 z-50'>
						{(isLoading || skipRequest || filtersAreFetching) && <LogoOverlay backgroundColor='white' />}
						{!isFetching ? (
							<SelectedDateRangeDisplay
								startDate={format(dateRange?.applied.startDate, 'M/d/yyyy')}
								endDate={format(dateRange?.applied.endDate, 'M/d/yyyy')}
							/>
						) : (
							<div className='p-2.5'>
								<WorkingIndicator size='lg' />
							</div>
						)}
						<Drawer
							metadata={metadata}
							filtersAreDirty={filtersAreDirty}
							trigger={
								<Button sizeX='sm' sizeY='md' variant={'primary-ghost'} className='mr-2'>
									<span className='material-symbol'>filter_alt</span>
									Filters
								</Button>
							}
							quickActions={[
								{
									icon: 'undo',
									onClick: resetFilters,
									tooltipText: 'Discard unapplied filter changes',
									disabled: !filtersAreDirty,
								},
								{
									icon: 'restart_alt',
									onClick: clearFilters,
									tooltipText: 'Reset filters to default',
									disabled: !metadata.saved_at,
								},
							]}
							actionButtons={[
								{
									onClick: applyFilters,
									children: 'Apply',
									disabled: !filtersAreDirty,
								},
								{
									// eslint-disable-next-line @typescript-eslint/no-empty-function
									onClick: () => {},
									children: 'Views',
									disabled: false,
								},
							]}
						>
							<FilterFields
								fields={['surgeons', 'wheelsTurnoverTimeThreshold', 'fcotsGraceMinutes', 'dateRange', 'procedures']}
							/>
						</Drawer>
						<div className='z-10'>
							{surgeons.applied.length !== 0 && !isLoading && exportData.length > 0 ? (
								<ButtonStack>
									<Button sizeX='md' sizeY='md' variant='primary'>
										<ExportButton no_button={true} sizeX='md' sizeY='md' variant='primary' contents={exportData}>
											Export CSV
										</ExportButton>
									</Button>

									<Button
										sizeX='md'
										sizeY='md'
										variant='primary'
										disabled={isBulkPrinting}
										onClick={() => {
											setIsBulkPrinting(true);
											setBulkPrintProgress(0);
											setBulkPrintStatusText('Preparing print pages...');
											setPrinting(true);
											setTimeout(() => {
												generatePDF();
											}, 0);
										}}
									>
										Export PDF
									</Button>
								</ButtonStack>
							) : (
								<span className='opacity-50 cursor-not-allowed'>
									<ExportButton contents={[]} />
								</span>
							)}
						</div>
					</div>
				)}
				{surgeons.applied.length !== 0 && data?.surgeonData[0] && (
					<React.Fragment>
						{/* <div className='grid grid-cols-3 gap-x-3 justify-center align-middle'>
						<BlockPanel title='Block' className='opacity-50 cursor-not-allowed' />
						<BlockPanel title='Volume by Month' />
						<BlockPanel title='Saved Views with Surgeon' className='opacity-50 cursor-not-allowed' />
						<PatternInfoBlock items={pattern_info} />
						<SurgeonVolumeBarGraph items={volume_graph_data} />
						<SavedViewsBlock items={saved_views} />
					</div> */}
						{!printing && !isLoading && surgeons.applied.length === 1 && data?.surgeonData[0] ? (
							<SingleSurgeonScorecardDashboard data={data.surgeonData[0]} lockTable={isFetching} />
						) : (
							!printing && (
								<SurgeonTables
									title={isFetching ? '' : `Surgeon Overview`}
									startDate={format(dateRange?.applied.startDate, 'M/d/yyyy')}
									endDate={format(dateRange?.applied.endDate, 'M/d/yyyy')}
									data={printableSurgeonData}
									lockTable={isFetching}
									minLimit={minLimit}
								/>
							)
						)}
					</React.Fragment>
				)}

				{printing && (
					<div className='fixed -left-[99999px] top-0 opacity-0 pointer-events-none' aria-hidden='true'>
						{printableSurgeonData.map((surgeonData, index) => (
							<PrintableSurgeonScorecardPage
								key={surgeonData.surgeon_name || index}
								data={surgeonData}
								lockTable={isFetching}
								dateRangeLabel={dateRangeLabel}
								facilitySystemLabel={facilitySystemLabel}
								isLastPage={index === printableSurgeonData.length - 1}
							/>
						))}
					</div>
				)}

				{surgeons.applied.length === 0 && (
					<React.Fragment>
						<img className='h-16 w-16 block m-auto mt-32' src={Logo} alt='Merlin' />
						<p className='text-p1 text-center mt-3'>Please select a surgeon using the filters button.</p>
					</React.Fragment>
				)}
			</div>
		</>
	);
}

export default SurgeonScorecard;
